import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import './Slider.css';
import commonServicers from "../images/common-servicers.png"

const Slider = () => {
  const slides = [
    {
      id: 1,
      title: 'Professional Marble & Granite Polishing',
      description: 'Transform your surfaces with our expert polishing services. We bring back the natural shine and beauty of your marble and granite.',
      image: 'linear-gradient(135deg, rgba(152, 2, 44, 0.9), rgba(122, 2, 31, 0.9))',
      badge: 'Premium Service',
      features: ['Expert Technicians', 'Premium Products', 'Satisfaction Guaranteed']
    },
    {
      id: 2,
      title: 'Complete Renovation Solutions',
      description: 'From planning to execution, we handle your entire renovation project with precision and care. Transform your space with modern design.',
      image: 'linear-gradient(135deg, rgba(152, 2, 44, 0.9), rgba(122, 2, 31, 0.9))',
      badge: 'Full Service',
      features: ['Design Consultation', 'Quality Materials', 'Timely Completion']
    },
    {
      id: 3,
      title: 'Deep Cleaning & Maintenance',
      description: 'Comprehensive cleaning services for homes and offices. We ensure a hygienic and spotless environment for your peace of mind.',
      image: 'linear-gradient(135deg, rgba(152, 2, 44, 0.9), rgba(122, 2, 31, 0.9))',
      badge: 'Eco-Friendly',
      features: ['Eco-Friendly Products', 'Thorough Cleaning', 'Regular Maintenance']
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const sliderContainerRef = React.useRef(null);
  const slideContainerRefs = React.useRef([]);

  // Update slider height based on active slide content
  useEffect(() => {
    let resizeTimeout;
    
    const updateSliderHeight = () => {
      const windowWidth = window.innerWidth;
      const isTabletOrMobile = windowWidth <= 1024;
      
      if (isTabletOrMobile) {
        // On tablet/mobile, let CSS handle it (slides are relative/display:none)
        if (sliderContainerRef.current) {
          sliderContainerRef.current.style.height = 'auto';
          const sliderContainer = sliderContainerRef.current.querySelector('.slider-container');
          if (sliderContainer) {
            sliderContainer.style.height = 'auto';
          }
        }
        return;
      }

      // Desktop: measure active slide and set container height dynamically
      const activeIndex = currentSlide;
      if (slideContainerRefs.current[activeIndex] && sliderContainerRef.current) {
        const containerElement = slideContainerRefs.current[activeIndex];
        const slideElement = containerElement.closest('.slide');
        
        if (slideElement && slideElement.classList.contains('active')) {
          // Get the Row element which contains all the actual content
          const rowElement = containerElement.querySelector('.row');
          if (!rowElement) return;
          
          // Force a layout calculation
          void rowElement.offsetHeight;
          
          // Measure multiple elements to get the most accurate height
          const cols = rowElement.querySelectorAll('.col, [class*="col-"]');
          let maxColHeight = 0;
          let totalColHeights = 0;
          
          cols.forEach(col => {
            // Get both scrollHeight and offsetHeight to account for different scenarios
            const scrollH = col.scrollHeight || 0;
            const offsetH = col.offsetHeight || 0;
            const colHeight = Math.max(scrollH, offsetH);
            maxColHeight = Math.max(maxColHeight, colHeight);
            totalColHeights += colHeight;
          });
          
          // Also measure the row itself
          const rowScrollHeight = rowElement.scrollHeight || 0;
          const rowOffsetHeight = rowElement.offsetHeight || 0;
          const rowHeight = Math.max(rowScrollHeight, rowOffsetHeight);
          
          // Use the maximum of: max column height, row height, or average column height
          // This handles various layout scenarios
          const avgColHeight = cols.length > 0 ? totalColHeights / cols.length : 0;
          const contentHeight = Math.max(maxColHeight, rowHeight, avgColHeight);
          
          // Get computed padding from slide
          const computedStyle = window.getComputedStyle(slideElement);
          const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
          const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
          
          // Get container padding/margins (usually Bootstrap containers have padding)
          const containerStyle = window.getComputedStyle(containerElement);
          const containerPaddingTop = parseFloat(containerStyle.paddingTop) || 0;
          const containerPaddingBottom = parseFloat(containerStyle.paddingBottom) || 0;
          const containerMarginTop = parseFloat(containerStyle.marginTop) || 0;
          const containerMarginBottom = parseFloat(containerStyle.marginBottom) || 0;
          
          // Calculate total height needed (content + all paddings and margins)
          const totalContentHeight = contentHeight + 
                                    paddingTop + paddingBottom + 
                                    containerPaddingTop + containerPaddingBottom +
                                    containerMarginTop + containerMarginBottom;
          
          // Get viewport height
          const viewportHeight = window.innerHeight;
          
          // For large screens (1400px+), be more flexible with height
          // Allow content to determine height, but ensure it's at least reasonable
          let calculatedHeight;
          
          if (windowWidth >= 1400) {
            // On very large screens, prioritize content height but keep it reasonable
            // Don't force viewport height if content is naturally smaller
            const minHeight = viewportHeight * 0.8; // Minimum 80% of viewport
            const maxHeight = viewportHeight * 1.2; // Maximum 120% of viewport to prevent excessive space
            
            // Use content height if it's within reasonable bounds
            if (totalContentHeight >= minHeight && totalContentHeight <= maxHeight) {
              calculatedHeight = totalContentHeight;
            } else if (totalContentHeight < minHeight) {
              // Content is too small, use minimum
              calculatedHeight = minHeight;
            } else {
              // Content is too large, cap it
              calculatedHeight = maxHeight;
            }
          } else if (windowWidth >= 1200) {
            // Medium-large screens: balance between content and viewport
            calculatedHeight = Math.max(totalContentHeight, viewportHeight * 0.9);
          } else {
            // Regular desktop screens, use viewport height as base but allow content to expand
            calculatedHeight = Math.max(totalContentHeight, viewportHeight);
          }
          
          if (calculatedHeight > 0 && !isNaN(calculatedHeight)) {
            sliderContainerRef.current.style.height = `${calculatedHeight}px`;
            const sliderContainer = sliderContainerRef.current.querySelector('.slider-container');
            if (sliderContainer) {
              sliderContainer.style.height = `${calculatedHeight}px`;
            }
          }
        }
      }
    };

    // Update after slide change and on mount
    // Use requestAnimationFrame to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      updateSliderHeight();
      // Double check after a brief delay to ensure content is rendered
      setTimeout(() => {
        requestAnimationFrame(updateSliderHeight);
      }, 100);
      // Check again after images potentially load
      setTimeout(() => {
        requestAnimationFrame(updateSliderHeight);
      }, 300);
    });
    
    // Update on window resize with debouncing
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateSliderHeight, 200);
    };

    // Handle image loading for accurate height calculation
    const handleImageLoad = () => {
      updateSliderHeight();
    };

    // Add image load listeners after a short delay to ensure DOM is ready
    const imageTimeout = setTimeout(() => {
      const images = sliderContainerRef.current?.querySelectorAll('img');
      if (images && images.length > 0) {
        images.forEach(img => {
          if (img.complete) {
            // Image already loaded, update height
            updateSliderHeight();
          } else {
            img.addEventListener('load', handleImageLoad, { once: true });
            img.addEventListener('error', handleImageLoad, { once: true });
          }
        });
      }
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimeout);
      clearTimeout(imageTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [currentSlide]);

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  };

  return (
    <section className="hero-slider" >
    {/* ref={sliderContainerRef} */}
      <div className="slider-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`slide ${index === currentSlide ? 'active' : ''}`}
            style={{ background: slide.image }}
          >
            <div className="slide-overlay"></div>
            <Container ref={(el) => (slideContainerRefs.current[index] = el)}>
              <Row className="align-items-center">
                <Col lg={6}>
                  <div className="slide-content">
                    <span className="slide-badge">{slide.badge}</span>
                    <h1 className="slide-title">{slide.title}</h1>
                    <p className="slide-description">{slide.description}</p>
                    <div className="slide-features">
                      {slide.features.map((feature, idx) => (
                        <div key={idx} className="slide-feature">
                          <FaCheckCircle className="feature-check-icon" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="slide-buttons">
                      <Button as={Link} to="/contact" variant="light" size="lg" className="slide-btn-primary">
                        Get Free Quote <FaArrowRight className="ms-2" />
                      </Button>
                      <Button as={Link} to="/services" variant="outline-light" size="lg" className="slide-btn-secondary">
                        Our Services
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="slide-image-placeholder">
                    <img className='w-100' src={commonServicers} alt="OM services"/>
                    {/* <div className="placeholder-content">
                      <div className="placeholder-icon">🏗️</div>
                      <p>Service Image</p>
                    </div> */}
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button className="slider-nav prev" onClick={prevSlide} aria-label="Previous slide">
        <FaArrowLeft />
      </button>
      <button className="slider-nav next" onClick={nextSlide} aria-label="Next slide">
        <FaArrowRight />
      </button>

      {/* Dots Indicator */}
      <div className="slider-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      {/* <div className="slider-progress">
        <div 
          className="slider-progress-bar" 
          style={{ 
            width: `${((currentSlide + 1) / slides.length) * 100}%`,
            transition: isAutoPlaying ? 'width 5s linear' : 'width 0.3s ease'
          }}
        />
      </div> */}
    </section>
  );
};

export default Slider;


